import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useCustomerInvoice, useFinalizeInvoice, useVoidInvoice, useGenerateInvoicePdf } from '@/hooks/invoicing';
import { useShopInvoicingSettings } from '@/hooks/invoicing/useCustomerInvoices';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ChevronLeft, FileDown, CheckCircle2, XCircle, RefreshCw } from 'lucide-react';
import { formatInvoiceNumber, documentKindUpperLabel } from '@/lib/invoicing/formatInvoiceNumber';
import { canVoid, canFinalize, canRequestPreAuth, canRequestPostAuth, canRecordPayment } from '@/lib/invoicing/invoiceStatusMachine';
import { InvoiceStatusBadge } from '@/components/invoicing/InvoiceStatusBadge';
import { AuthorizationModal } from '@/components/invoicing/AuthorizationModal';
import { PaymentDialog } from '@/components/invoicing/PaymentDialog';
import { RefundDialog } from '@/components/invoicing/RefundDialog';
import { usePermissions } from '@/hooks/usePermissions';
import { useExposureFlag } from '@/hooks/useFeatureFlags';

const InvoiceDetail = () => {
  const { id } = useParams<{ id: string }>();
  const enabled = useExposureFlag('ui_customer_invoices');
  const { canManageCustomerInvoices, canVoidCustomerInvoice, canIssueCustomerInvoiceRefund, canViewCustomerInvoices } = usePermissions();
  const { data, isLoading } = useCustomerInvoice(id, enabled && canViewCustomerInvoices);
  const { data: settings } = useShopInvoicingSettings();
  const finalize = useFinalizeInvoice();
  const voidMut = useVoidInvoice();
  const genPdf = useGenerateInvoicePdf();
  const [authModal, setAuthModal] = useState<null | 'pre_repair' | 'post_repair'>(null);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);

  if (!enabled || !canViewCustomerInvoices) {
    return <div className="p-8 text-muted-foreground">Customer invoicing is not enabled for this shop.</div>;
  }
  if (isLoading || !data?.header) {
    return <div className="p-8 text-muted-foreground">Loading…</div>;
  }
  const inv = data.header;
  const hasPreAuth = data.authorizations.some((a) => a.type === 'pre_repair');
  const hasPostAuth = data.authorizations.some((a) => a.type === 'post_repair');
  const status = inv.status as any;

  const preAuthText = settings?.pre_repair_terms ?? 'I authorize the work above.';
  const postAuthText = settings?.post_repair_terms ?? 'I confirm the work is complete and accept the vehicle.';

  const scopePreview = data.lines
    .filter((l) => l.type !== 'tax')
    .map((l) => ({ description: l.description, extended_total: Number(l.extended_total), type: l.type }));

  const handleVoid = async () => {
    const reason = prompt('Reason for voiding this invoice?');
    if (!reason) return;
    await voidMut.mutateAsync({ invoiceId: inv.id, reason });
  };

  const handleDownload = async () => {
    const res = await genPdf.mutateAsync({ invoiceId: inv.id });
    if (res.url) window.open(res.url, '_blank');
  };

  return (
    <div className="container max-w-5xl mx-auto p-4 space-y-4">
      <div className="flex items-center justify-between">
        <Button asChild variant="ghost" size="sm">
          <Link to="/invoices"><ChevronLeft className="h-4 w-4 mr-1" /> Back to invoices</Link>
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleDownload} disabled={genPdf.isPending}>
            <FileDown className="h-4 w-4 mr-1" /> {genPdf.isPending ? 'Generating…' : 'Download PDF'}
          </Button>
          {canVoidCustomerInvoice && canVoid(status) && (
            <Button variant="destructive" size="sm" onClick={handleVoid}>
              <XCircle className="h-4 w-4 mr-1" /> Void
            </Button>
          )}
        </div>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              {documentKindUpperLabel(inv.document_kind)} {formatInvoiceNumber(inv)}
              <InvoiceStatusBadge status={status} />
            </CardTitle>
            <div className="text-sm text-muted-foreground mt-1">
              Ticket {inv.ticket_id} · created {new Date(inv.created_at).toLocaleString()}
            </div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold tabular-nums">${Number(inv.grand_total).toFixed(2)}</div>
            {Number(inv.balance_due) > 0 && (
              <div className="text-sm text-destructive">Balance due ${Number(inv.balance_due).toFixed(2)}</div>
            )}
            {Number(inv.credit_balance) > 0 && (
              <div className="text-sm text-primary">Credit on file ${Number(inv.credit_balance).toFixed(2)}</div>
            )}
          </div>
        </CardHeader>
        <CardContent className="grid sm:grid-cols-2 gap-4 text-sm">
          <div>
            <div className="font-medium mb-1">Customer</div>
            <div>{inv.customer_name ?? '—'}</div>
            <div className="text-muted-foreground">{inv.customer_phone ?? ''}</div>
            <div className="text-muted-foreground">{inv.customer_email ?? ''}</div>
          </div>
          <div>
            <div className="font-medium mb-1">Vehicle</div>
            <div>
              {(inv.vehicle_snapshot as any)?.year} {(inv.vehicle_snapshot as any)?.make} {(inv.vehicle_snapshot as any)?.model}
            </div>
            {(inv.vehicle_snapshot as any)?.vin && (
              <div className="text-muted-foreground">VIN {(inv.vehicle_snapshot as any).vin}</div>
            )}
            {/* THIS visit's odometer reading, as recorded on the ticket — never a later visit's */}
            <div className="text-muted-foreground" data-testid="invoice-mileage">
              {typeof (inv.vehicle_snapshot as any)?.odometer === 'number'
                ? `Mileage: ${((inv.vehicle_snapshot as any).odometer as number).toLocaleString()} mi`
                : 'Mileage: not recorded for this visit'}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Line items</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-1">
            {data.lines.map((l) => (
              <div key={l.id} className="grid grid-cols-12 text-sm py-1 border-b last:border-0">
                <div className="col-span-1 text-muted-foreground tabular-nums">{l.line_number}</div>
                <div className="col-span-1 uppercase text-xs text-muted-foreground">{l.type}</div>
                <div className="col-span-5">{l.description}</div>
                <div className="col-span-1 tabular-nums text-right">{Number(l.quantity).toFixed(2)}</div>
                <div className="col-span-2 tabular-nums text-right">${Number(l.unit_price).toFixed(2)}</div>
                <div className="col-span-2 tabular-nums text-right font-medium">${Number(l.extended_total).toFixed(2)}</div>
              </div>
            ))}
          </div>
          <Separator className="my-3" />
          <div className="space-y-1 text-sm">
            <Row label="Parts subtotal" value={inv.parts_subtotal} />
            <Row label="Labor subtotal" value={inv.labor_subtotal} />
            <Row label="Fees" value={inv.fees_total} />
            <Row label="Tax" value={inv.tax_total} />
            <Row label="Grand total" value={inv.grand_total} bold />
            <Row label="Amount paid" value={inv.amount_paid} />
            <Row label="Balance due" value={inv.balance_due} bold />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Authorizations</CardTitle>
          <div className="flex gap-2">
            {canManageCustomerInvoices && canRequestPreAuth(status) && !hasPreAuth && (
              <Button size="sm" onClick={() => setAuthModal('pre_repair')}>Request pre-repair signature</Button>
            )}
            {canManageCustomerInvoices && canFinalize(status, hasPreAuth) && (
              <Button
                size="sm"
                onClick={() =>
                  // Slice 5D-6: pass `ticketId` so the underlying
                  // `useFinalizeInvoice` hook can fire-and-forget an
                  // `invoice_sent` advisor action event when the
                  // advanced-capture flag is on. Non-blocking — the
                  // primary finalize call is unaffected if the emit
                  // fails or the flag is off.
                  finalize.mutateAsync({ invoiceId: inv.id, ticketId: inv.ticket_id })
                }
              >
                <CheckCircle2 className="h-4 w-4 mr-1" /> Finalize invoice
              </Button>
            )}
            {canManageCustomerInvoices && canRequestPostAuth(status) && !hasPostAuth && (
              <Button size="sm" variant="outline" onClick={() => setAuthModal('post_repair')}>
                Request post-repair signature
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          {data.authorizations.length === 0 && (
            <div className="text-sm text-muted-foreground">No signatures captured yet.</div>
          )}
          {data.authorizations.map((a) => (
            <div key={a.id} className="border rounded-md p-3 text-sm">
              <div className="flex items-center justify-between">
                <Badge variant="outline">{a.type === 'pre_repair' ? 'Pre-repair' : 'Post-repair'}</Badge>
                <div className="text-muted-foreground text-xs">
                  signed {new Date(a.signed_at).toLocaleString()}
                </div>
              </div>
              <div className="mt-1">By <span className="font-medium">{a.customer_name}</span></div>
              <div className="text-xs text-muted-foreground">
                Authorized ${Number(a.authorized_amount).toFixed(2)} · IP {a.ip_address ?? 'n/a'}
              </div>
              {a.signature_png && (
                <img src={a.signature_png} alt="signature"
                  className="mt-2 max-h-24 border rounded bg-white" />
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Payments</CardTitle>
          <div className="flex gap-2">
            {canManageCustomerInvoices && canRecordPayment(status) && Number(inv.balance_due) > 0 && (
              <Button size="sm" onClick={() => setPaymentOpen(true)}>Record payment</Button>
            )}
            {canIssueCustomerInvoiceRefund && Number(inv.amount_paid) > 0 && (
              <Button size="sm" variant="destructive" onClick={() => setRefundOpen(true)}>
                <RefreshCw className="h-4 w-4 mr-1" /> Refund
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-1">
          {data.payments.length === 0 && (
            <div className="text-sm text-muted-foreground">No payments recorded.</div>
          )}
          {data.payments.map((p) => (
            <div key={p.id} className="flex items-center justify-between text-sm py-1 border-b last:border-0">
              <div className="flex items-center gap-2">
                <Badge variant={p.kind === 'refund' ? 'destructive' : 'secondary'}>
                  {p.kind}
                </Badge>
                <span className="text-muted-foreground">{new Date(p.recorded_at).toLocaleString()}</span>
                <span>{p.method}</span>
                {p.notes && <span className="text-muted-foreground italic">— {p.notes}</span>}
              </div>
              <div className={`tabular-nums font-medium ${p.kind === 'refund' ? 'text-destructive' : ''}`}>
                ${Number(p.applied_amount).toFixed(2)}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Audit trail</CardTitle></CardHeader>
        <CardContent className="space-y-1 text-xs">
          {data.audit.map((e) => (
            <div key={e.id} className="flex items-start gap-2 py-0.5">
              <span className="text-muted-foreground tabular-nums w-40">
                {new Date(e.created_at).toLocaleString()}
              </span>
              <Badge variant="outline" className="text-[10px]">{e.event_type}</Badge>
            </div>
          ))}
        </CardContent>
      </Card>

      {authModal && (
        <AuthorizationModal
          open={!!authModal}
          onOpenChange={(o) => !o && setAuthModal(null)}
          invoiceId={inv.id}
          type={authModal}
          authorizationText={authModal === 'pre_repair' ? preAuthText : postAuthText}
          authorizedAmount={Number(inv.grand_total)}
          approvedScopePreview={scopePreview}
          defaultCustomerName={inv.customer_name}
          // Slice 5D-6: thread ticket_id through so
          // `customer_approval_received` advisor action can fire on
          // successful signature capture.
          ticketId={inv.ticket_id}
        />
      )}
      <PaymentDialog
        open={paymentOpen} onOpenChange={setPaymentOpen}
        invoiceId={inv.id} balanceDue={Number(inv.balance_due)}
      />
      <RefundDialog
        open={refundOpen} onOpenChange={setRefundOpen}
        invoiceId={inv.id} amountPaid={Number(inv.amount_paid)} payments={data.payments}
      />
    </div>
  );
};

const Row = ({ label, value, bold }: { label: string; value: number; bold?: boolean }) => (
  <div className={`flex justify-between ${bold ? 'font-semibold' : ''}`}>
    <span>{label}</span>
    <span className="tabular-nums">${Number(value).toFixed(2)}</span>
  </div>
);

export default InvoiceDetail;
